import { useEffect, useMemo, useRef, useState } from "react";
import { CHART_CONFIG } from "./config";

/**
 * cubic-bezier(x1, y1, x2, y2) solved for y at a given x, the way CSS does it:
 * Newton-Raphson to invert the x polynomial, then evaluate y at that t.
 */
function cubicBezier(x1, y1, x2, y2) {
  const a = (p, q) => 1 - 3 * q + 3 * p;
  const b = (p, q) => 3 * q - 6 * p;
  const c = (p) => 3 * p;
  const at = (t, p, q) => ((a(p, q) * t + b(p, q)) * t + c(p)) * t;
  const slope = (t, p, q) => 3 * a(p, q) * t * t + 2 * b(p, q) * t + c(p);

  return (x) => {
    if (x <= 0) return 0;
    if (x >= 1) return 1;
    let t = x;
    for (let i = 0; i < 8; i++) {
      const d = slope(t, x1, x2);
      if (Math.abs(d) < 1e-6) break;
      // Clamped to the curve's domain — with x1 or x2 at 0 (our default curve),
      // x(t) is nearly flat at the origin and an unclamped step diverges wildly.
      t = Math.min(1, Math.max(0, t - (at(t, x1, x2) - x) / d));
    }
    return at(t, y1, y2);
  };
}

/**
 * Eases a per-segment 0..1 value toward its target on every frame.
 *
 * Everything moving at a given moment shares one clock, so a reveal and the
 * labels riding on it stay in step. Retargeting mid-flight restarts from
 * wherever each value had got to, so an interrupted transition never snaps.
 *
 * @param ids Segment ids that should be at 1 — a single id, or the whole
 * selection path when several rings are open at once.
 */
export function useHoverTween(ids, transition = CHART_CONFIG.transition) {
  // Joined so the effect keys off the contents, not the object identity.
  const key = Array.isArray(ids) ? ids.join(" ") : ids ?? "";
  const [values, setValues] = useState({});
  const ref = useRef(values);

  const ease = useMemo(
    () => cubicBezier(transition.ease[0], transition.ease[1], transition.ease[2], transition.ease[3]),
    [transition]
  );

  useEffect(() => {
    const targets = new Set(key ? key.split(" ") : []);
    const moving = new Set([...Object.keys(ref.current), ...targets]);
    if (moving.size === 0) return;

    // Where each value starts this run, so an interruption picks up smoothly.
    const from = {};
    for (const id of moving) from[id] = ref.current[id] ?? 0;

    const durationMs = transition.duration * 1000;

    let raf = 0;
    // Seeded from the first frame, not from now: rAF reports the frame's start,
    // which can predate an effect that ran later in that same frame.
    let start = 0;
    const current = { ...from };

    const step = (now) => {
      if (start === 0) start = now;

      let settling = false;
      const next = {};

      for (const id of moving) {
        const target = targets.has(id) ? 1 : 0;
        const p = durationMs <= 0 ? 1 : Math.min(1, (now - start) / durationMs);
        current[id] = from[id] + (target - from[id]) * ease(p);
        if (p < 1) settling = true;
        else current[id] = target;

        // Drop ids that have finished retreating, so they stop being drawn.
        if (!settling && target === 0 && current[id] === 0) continue;
        next[id] = current[id];
      }

      ref.current = next;
      setValues(next);
      raf = settling ? requestAnimationFrame(step) : 0;
    };

    raf = requestAnimationFrame(step);
    return () => cancelAnimationFrame(raf);
  }, [key, ease, transition.duration]);

  return values;
}
